import {useEffect, useState} from "react";
import i18n from "../../i18n";
import {useTranslation} from "react-i18next";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PublicIcon from "@mui/icons-material/Public";
import {StatusChip} from "../ui/StatusChip";
import {colors, fonts} from "../../theme/tokens";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type {SmsCountryFallbackSenderId, SmsSenderPolicy} from "@calendall/shared";
import {getSmsSenderPolicy, updateSmsSenderPolicy} from "../../api/smsSenderApi";
import {isValidCountryCode, isValidCountryFallbackSenderId, isValidSenderId} from "./smsSenderValidation";

function isValidCountryFallbackList(entries: SmsCountryFallbackSenderId[]): boolean {
    return entries.every((entry) => isValidCountryCode(entry.country) && isValidCountryFallbackSenderId(entry.senderId))
            && new Set(entries.map((entry) => entry.country)).size === entries.length;
}

function serializeCountryFallbacks(entries: SmsCountryFallbackSenderId[]): string {
    return entries.map((entry) => `${entry.country}=${entry.senderId}`).join(",");
}

function parseCountryList(value: string): string[] {
    return value
            .split(",")
            .map((code) => code.trim())
            .filter((code) => code.length > 0);
}

export function SmsSenderPolicyCard() {
    const {t} = useTranslation();
    const [saved, setSaved] = useState<SmsSenderPolicy | null>(null);
    const [fallbackSenderId, setFallbackSenderId] = useState("");
    const [countries, setCountries] = useState("");
    const [countryFallbacks, setCountryFallbacks] = useState<SmsCountryFallbackSenderId[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    function showPolicy(policy: SmsSenderPolicy) {
        setSaved(policy);
        setFallbackSenderId(policy.fallbackSenderId);
        setCountries(policy.countriesAllowingUnregisteredSenderIds.join(", "));
        setCountryFallbacks(policy.countryFallbackSenderIds);
    }

    useEffect(() => {
        getSmsSenderPolicy()
                .then(showPolicy)
                .catch((e: unknown) => setError(e instanceof Error ? e.message : i18n.t("SMS_SENDER_POLICY_LOAD_ERROR")));
    }, []);

    const countryList = parseCountryList(countries);
    const fallbackInvalid = !isValidSenderId(fallbackSenderId);
    const countriesInvalid = !countryList.every(isValidCountryCode) || new Set(countryList).size !== countryList.length;
    const countryFallbacksInvalid = !isValidCountryFallbackList(countryFallbacks);
    const hasChanges = saved !== null && (
            fallbackSenderId !== saved.fallbackSenderId
            || countryList.join(",") !== saved.countriesAllowingUnregisteredSenderIds.join(",")
            || serializeCountryFallbacks(countryFallbacks) !== serializeCountryFallbacks(saved.countryFallbackSenderIds)
    );

    function changeCountryFallback(index: number, change: Partial<SmsCountryFallbackSenderId>) {
        setCountryFallbacks((entries) => entries.map((entry, entryIndex) => entryIndex === index ? {...entry, ...change} : entry));
    }

    async function save() {
        setSaving(true);
        setError(null);
        setSuccess(false);
        try {
            showPolicy(await updateSmsSenderPolicy({
                fallbackSenderId,
                countriesAllowingUnregisteredSenderIds: countryList,
                countryFallbackSenderIds: countryFallbacks,
            }));
            setSuccess(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : t("SMS_SENDER_POLICY_SAVE_ERROR"));
        } finally {
            setSaving(false);
        }
    }

    return (
            <Paper variant="outlined" component="section" aria-labelledby="sms-sender-policy-title" data-testid="sms-sender-policy" sx={{borderTop: `3px solid ${colors.primary}`, overflow: "hidden"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", px: 2, py: 1.5, borderBottom: `1px solid ${colors.sectionDivider}`}}>
                    <Typography variant="h6" component="h2" id="sms-sender-policy-title">{t("SMS_SENDER_POLICY")}</Typography>
                    <Box sx={{display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: "12.5px", color: colors.warningText}}>
                        <PublicIcon sx={{fontSize: 16}} />
                        {t("SMS_SENDER_POLICY_SCOPE_BANNER")}
                    </Box>
                </Box>
                <Box sx={{p: 2}}>
                    {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{mb: 2}}>{t("SMS_SENDER_POLICY_SAVED")}</Alert>}
                    {saved && (
                            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", lg: "1fr 1fr"}, gap: 4}}>
                                <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                    <Typography variant="body2" sx={{color: colors.body}}>{t("SMS_SENDER_POLICY_HINT")}</Typography>
                                    <TextField
                                            size="small"
                                            label={t("SMS_SENDER_FALLBACK_SENDER_ID")}
                                            value={fallbackSenderId}
                                            onChange={(e) => setFallbackSenderId(e.target.value)}
                                            error={fallbackInvalid}
                                            helperText={t("SMS_SENDER_ID_RULES")}
                                    />
                                    <Box>
                                        <TextField
                                                size="small"
                                                fullWidth
                                                label={t("SMS_SENDER_UNREGISTERED_COUNTRIES")}
                                                value={countries}
                                                onChange={(e) => setCountries(e.target.value.toUpperCase())}
                                                error={countriesInvalid}
                                                helperText={t("SMS_SENDER_UNREGISTERED_COUNTRIES_HINT")}
                                        />
                                        {countryList.length > 0 && (
                                                <Box aria-hidden sx={{display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1}}>
                                                    {countryList.map((code, index) => {
                                                        const problem = !isValidCountryCode(code) || countryList.indexOf(code) !== index;
                                                        return (
                                                                <StatusChip key={`${code}-${index}`} tone={problem ? "error" : "neutral"} mono>
                                                                    {code}
                                                                </StatusChip>
                                                        );
                                                    })}
                                                </Box>
                                        )}
                                    </Box>
                                </Box>
                                <Box sx={{display: "flex", flexDirection: "column", gap: 1.25}}>
                                    <Box>
                                        <Typography variant="h6" component="h3">{t("SMS_SENDER_COUNTRY_FALLBACKS")}</Typography>
                                        <Typography variant="body2" sx={{color: colors.muted}}>{t("SMS_SENDER_COUNTRY_FALLBACKS_HINT")}</Typography>
                                    </Box>
                                    {countryFallbacks.map((entry, index) => {
                                        // Every accessible name in a row contains its position, the only identifier that stays unique
                                        // while two rows name the same country and stable while a country is being typed.
                                        const rowNumber = index + 1;
                                        const rowName = entry.country ? `${rowNumber} (${entry.country})` : String(rowNumber);
                                        return (
                                                <Box
                                                        key={index}
                                                        role="group"
                                                        aria-label={t("SMS_SENDER_COUNTRY_FALLBACK_ROW", {name: rowName})}
                                                        sx={{display: "grid", gridTemplateColumns: "96px 1fr auto", alignItems: "flex-start", gap: 1.5}}
                                                >
                                                    <TextField
                                                            size="small"
                                                            label={t("SMS_SENDER_COUNTRY")}
                                                            value={entry.country}
                                                            onChange={(e) => changeCountryFallback(index, {country: e.target.value.toUpperCase()})}
                                                            slotProps={{htmlInput: {"aria-label": t("SMS_SENDER_COUNTRY_FALLBACK_COUNTRY_FIELD", {number: rowNumber}), style: {fontFamily: fonts.mono}}}}
                                                            error={!isValidCountryCode(entry.country)}
                                                    />
                                                    <TextField
                                                            size="small"
                                                            label={t("SMS_SENDER_COUNTRY_FALLBACK_SENDER")}
                                                            value={entry.senderId}
                                                            onChange={(e) => changeCountryFallback(index, {senderId: e.target.value})}
                                                            slotProps={{htmlInput: {"aria-label": t("SMS_SENDER_COUNTRY_FALLBACK_SENDER_FIELD", {number: rowNumber})}}}
                                                            error={!isValidCountryFallbackSenderId(entry.senderId)}
                                                    />
                                                    <IconButton
                                                            color="error"
                                                            aria-label={t("SMS_SENDER_REMOVE_COUNTRY_FALLBACK", {name: rowName})}
                                                            onClick={() => setCountryFallbacks((entries) => entries.filter((_, entryIndex) => entryIndex !== index))}
                                                            sx={{mt: 2.75}}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                        );
                                    })}
                                    <Box>
                                        <Button
                                                variant="text"
                                                startIcon={<AddIcon />}
                                                onClick={() => setCountryFallbacks((entries) => [...entries, {country: "", senderId: ""}])}
                                        >
                                            {t("SMS_SENDER_ADD_COUNTRY_FALLBACK")}
                                        </Button>
                                    </Box>
                                    {countryFallbacksInvalid && (
                                            <Typography variant="body2" color="error">
                                                {t("SMS_SENDER_COUNTRY_FALLBACKS_RULES")}
                                            </Typography>
                                    )}
                                    <Box sx={{display: "flex", justifyContent: "flex-end", mt: 1}}>
                                        <Button
                                                variant="contained"
                                                onClick={() => void save()}
                                                disabled={!hasChanges || fallbackInvalid || countriesInvalid || countryFallbacksInvalid || saving}
                                        >
                                            {saving ? t("DETAIL_SAVING") : t("SMS_SENDER_SAVE_POLICY")}
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                    )}
                </Box>
            </Paper>
    );
}
